import { useQuery } from '@apollo/client';

import { getFormattedCourseDataFromQuery } from 'bundles/enroll-common/components/xdp/enrollSectionUtils';
import GetCourseForXdpEnrollCta from 'bundles/enroll-common/queries/GetCourseForXdpEnrollCtaQuery.graphql';
import GetSpecializationForXdpEnrollCta from 'bundles/enroll-common/queries/GetSpecializationForXdpEnrollCtaQuery.graphql';
import type {
  GetCourseForXdpEnrollCtaQuery,
  GetCourseForXdpEnrollCtaQueryVariables,
} from 'bundles/enroll-common/queries/__generated__/GetCourseForXdpEnrollCtaQuery';
import type {
  GetSpecializationForXdpEnrollCtaQuery,
  GetSpecializationForXdpEnrollCtaQueryVariables,
} from 'bundles/enroll-common/queries/__generated__/GetSpecializationForXdpEnrollCtaQuery';
import type { SpecializationForXdpEnrollCtaFragment } from 'bundles/enroll-common/queries/__generated__/SharedCommonEntitiesFragments';
import type { FormattedCourse } from 'bundles/enroll-common/types/enrollSectionTypes';

type UseEnrollButtonQueriesProps = {
  courseId?: string;
  s12nId?: string;
  isSpecialization: boolean;
};

export type UseEnrollButtonQueriesResult = {
  course?: FormattedCourse;
  s12ns?: SpecializationForXdpEnrollCtaFragment[];
  s12n?: SpecializationForXdpEnrollCtaFragment;
  loading: boolean;
};

/**
 * Fetches the Course and Specialization GraphQL entities used to render the XDP enroll CTA.
 *
 * - The Course query checks whether a course is launched/pre-enrollment enabled and is used for
 *   standalone courses and courses-in-s12ns.
 * - The Specialization query checks whether an s12n is pre-enrollment enabled when the learner is
 *   logged out.
 *
 * Replaces the two `graphql()` HOC layers previously wired into `withEnrollButtonData`.
 */
export const useEnrollButtonQueries = ({
  courseId,
  s12nId,
  isSpecialization,
}: UseEnrollButtonQueriesProps): UseEnrollButtonQueriesResult => {
  const { data: courseData, loading: courseLoading } = useQuery<
    GetCourseForXdpEnrollCtaQuery,
    GetCourseForXdpEnrollCtaQueryVariables
  >(GetCourseForXdpEnrollCta, {
    skip: !(courseId && !isSpecialization),
    variables: { id: courseId! },
  });

  const { data: s12nData, loading: s12nLoading } = useQuery<
    GetSpecializationForXdpEnrollCtaQuery,
    GetSpecializationForXdpEnrollCtaQueryVariables
  >(GetSpecializationForXdpEnrollCta, {
    skip: !(s12nId && isSpecialization),
    variables: { id: s12nId! },
  });

  const { course, s12ns } = getFormattedCourseDataFromQuery(courseData);

  return {
    course,
    s12ns,
    s12n: s12nData?.Specialization?.queryById ?? undefined,
    loading: courseLoading || s12nLoading,
  };
};

export default useEnrollButtonQueries;
